/* eslint-disable complexity */
/* eslint-disable react/jsx-one-expression-per-line */
import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import { Avatar, Button, ConfigProvider, Dropdown, Layout, Menu } from 'antd';
import { Header } from 'antd/lib/layout/layout';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import App, { AppContext, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { AiOutlineArrowLeft, AiOutlineUser } from 'react-icons/ai';

import TransparentButton from '@/atoms/button-transparent';
import DynamicLoader from '@/atoms/dynamic-loader';
import { useApollo } from '@/lib/apollo';

import '../styles/tailwind.css';
import useGeoLocaleFallback from '@/common/hooks/useGeoLocaleFallback';
import LanguageSwitcher from '@/molecules/language-switcher';
import HeaderMenu from '@/molecules/header-menu';
import { getI18nInstance } from '@/lib/i18n';
import { ANTD_LOCALES } from '@/lib/i18n/antd';
import { detectLocale } from '@/lib/i18n/detect';
import { DAYJS_LOCALES, getDirection, SupportedLocale } from '@/lib/i18n/settings';
import { useAuth } from '@core/auth/store/auth';
import { getClientLocations } from '@core/locations/actions/getLocations';
import { useLocationsStore } from '@core/locations/store/locations';
import { getHomeSettings, getUISettings } from '@core/settings/actions/getSettings';
import { useSettingsStore } from '@core/settings/store/settings';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(utc);
dayjs.extend(timezone);

const { Content } = Layout;

const LogoNoSSR = dynamic(() => import('@/atoms/logo'), {
  ssr: false,
  loading: () => <DynamicLoader size="small" />,
});

interface MainAppProps extends AppProps {
  initialLocale: SupportedLocale;
  needsGeoFallback: boolean;
}

const MainApp = ({ Component, pageProps, initialLocale, needsGeoFallback }: MainAppProps) => {
  const [isReadyToBoot, setIsReadyToBoot] = useState(false);
  const [getSettingsLoading, setGetSettingsLoading] = useState(false);
  const router = useRouter();
  const { clientName } = router.query;
  const isWidget = router.pathname.includes('widget');
  const apolloClient = useApollo(pageProps);
  const {
    state: { user },
  } = useAuth();
  const { setSettings, setUISettings, getSettings, resetSettings, backgroundColor, textColor, topBarHeight } =
    useSettingsStore();
  const { setLocations, getLocations } = useLocationsStore();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const i18n = useMemo(() => getI18nInstance(initialLocale), []);
  const [locale, setLocale] = useState<SupportedLocale>((i18n.language as SupportedLocale) || initialLocale);
  const direction = getDirection(locale);
  // Render-time assignment so SSR formats dates in the request language.
  dayjs.locale(DAYJS_LOCALES[locale] || 'en');

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => setLocale(lng as SupportedLocale);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  useGeoLocaleFallback(i18n, needsGeoFallback);

  const isHome = useMemo(() => router.pathname === '/home', [router.pathname]);
  const headerHeight = useMemo(
    () => (topBarHeight != null ? Math.round(80 * (topBarHeight / 100)) : 80),
    [topBarHeight],
  );

  const gotoHome = useCallback(() => {
    router.push(`/home?clientName=${clientName}`);
  }, [clientName]);

  useEffect(() => {
    const sessionClientName = sessionStorage.getItem('clientName');
    if (clientName && sessionClientName !== clientName) {
      sessionStorage.setItem('clientName', clientName as string);
    }
    if (clientName) {
      getUISettings(clientName as string)
        .then(({ UISettings }) => {
          setUISettings(UISettings || []);
        })
        .catch((error) => {
          Sentry.captureException(error);
          resetSettings();
        });
    }
    if ((!getSettings() && clientName) || (clientName && sessionClientName !== clientName)) {
      setIsReadyToBoot(false);
      setGetSettingsLoading(true);
      getHomeSettings(clientName as string)
        .then(({ settings }) => {
          setSettings(settings);
          setGetSettingsLoading(false);
          setIsReadyToBoot(true);
        })
        .catch((error) => {
          Sentry.captureException(error);
          setIsReadyToBoot(true);
          setGetSettingsLoading(false);
          resetSettings();
        });
    } else if (!getSettingsLoading && clientName) {
      setIsReadyToBoot(true);
    }
  }, [clientName]);

  useEffect(() => {
    if (!getLocations().length && clientName) {
      getClientLocations(clientName as string)
        .then((locations) => {
          setLocations(locations);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [clientName]);

  useEffect(() => {
    process.on('unhandledRejection', (e: Error): void => {
      console.error(e);
    });
    process.on('uncaughtException', (e: Error): void => {
      console.error(e);
    });
    return () => {
      process.removeAllListeners('unhandledRejection');
      process.removeAllListeners('uncaughtException');
    };
  }, []);

  if (!isReadyToBoot) {
    return (
      <div className="flex justify-center items-center h-screen">
        <DynamicLoader size="large" />
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ConfigProvider locale={ANTD_LOCALES[locale]} direction={direction}>
        <ApolloProvider client={apolloClient}>
          {isWidget ? (
            <Component {...pageProps} />
          ) : (
            <div
              className="main-scroll flex flex-col h-screen"
              style={{
                width: '100%',
                backgroundColor: backgroundColor || undefined,
                color: textColor,
              }}
            >
              <Header
                className="flex !px-0 md:!px-[50px] items-center justify-center border-0 border-b-[1px] border-solid"
                style={{
                  height: headerHeight,
                  background: backgroundColor || 'transparent',
                  borderColor: backgroundColor || undefined,
                }}
              >
                <div className="flex mt-1 me-5 items-center justify-center md:hidden">
                  <Button
                    type="link"
                    onClick={() => {
                      router.back();
                    }}
                    icon={<AiOutlineArrowLeft className="rtl:rotate-180" size="20px" color={textColor} />}
                  />
                </div>
                <TransparentButton
                  onClick={gotoHome}
                  className="hover:!bg-transparent !cursor-pointer flex justify-center items-center"
                  style={{ height: headerHeight }}
                >
                  <LogoNoSSR height={Math.round(headerHeight * 0.69)} width={Math.round(headerHeight * 1.5)} />
                </TransparentButton>
                {/* TODO refactor header with our own */}
                <HeaderMenu />
                <div className="flex items-center ms-auto me-5" style={{ fontSize: '12px' }}>
                  <LanguageSwitcher variant="header" iconColor={textColor} />
                  {user ? (
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item key="0">
                            <Link href="/profile">
                              <a>{i18n.t('header.profile')}</a>
                            </Link>
                          </Menu.Item>
                          <Menu.Item key="1">
                            <Link href="/logout">
                              <a>{i18n.t('header.logout')}</a>
                            </Link>
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={['click']}
                    >
                      <div
                        className="flex items-center justify-center bg-red-200 cursor-pointer"
                        style={{ height: headerHeight }}
                      >
                        <Avatar style={{ backgroundColor: '#87d068' }} icon={<AiOutlineUser />} src={user?.imageLink} />
                        <span className="ms-2 text-xs">{user?.name}</span>
                      </div>
                    </Dropdown>
                  ) : (
                    <>
                      <Link
                        className="hidden md:flex items-center ms-auto me-5"
                        href={`https://${clientName}.pickspace.com/#/login`}
                        target="_blank"
                        style={{ color: textColor }}
                      >
                        <AiOutlineUser size={20} color={textColor} />
                        <span className="ms-2" style={{ fontSize: '20px' }}>
                          {i18n.t('header.login')}
                        </span>
                      </Link>
                      <Button
                        className="flex md:hidden justify-center items-center ms-auto"
                        style={{ height: headerHeight }}
                        type="link"
                        onClick={() => {
                          window.close();
                        }}
                      >
                        <AiOutlineUser color={textColor} size={20} />
                        <span className="ms-2" style={{ fontSize: '16px', color: textColor }}>
                          {i18n.t('header.login')}
                        </span>
                      </Button>
                    </>
                  )}
                </div>
              </Header>
              <Content
                className={`flex flex-1 col-span-3 relative h-full overflow-y-auto no-scrollbar ${
                  isHome ? '' : 'mx-0 md:mx-12'
                }`}
              >
                <Component {...pageProps} />
              </Content>
              <LanguageSwitcher />
            </div>
          )}
        </ApolloProvider>
      </ConfigProvider>
    </I18nextProvider>
  );
};

MainApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const { locale, needsGeoFallback } = detectLocale(appContext.ctx.query || {}, appContext.ctx.req);
  return { ...appProps, initialLocale: locale, needsGeoFallback };
};

export default MainApp;
